import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Print as PrintIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const InvoiceManagement = ({ showSnackbar, setLoading, loading }) => {
  const [filters, setFilters] = useState({
    invoiceNo: '',
    labName: '',
    status: '',
    dateFrom: null,
    dateTo: null,
  });

  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);

      // Generate mock invoice data
      const mockInvoices = [];
      for (let i = 1; i <= 25; i++) {
        mockInvoices.push({
          id: i,
          invoiceNo: `INV-2026-${String(i).padStart(4, '0')}`,
          labName: `Laboratory ${i}`,
          labId: `LAB${String(i).padStart(4, '0')}`,
          invoiceDate: dayjs().subtract(Math.floor(Math.random() * 90), 'days').format('YYYY-MM-DD'),
          dueDate: dayjs().add(Math.floor(Math.random() * 30), 'days').format('YYYY-MM-DD'),
          amount: (Math.random() * 50000 + 10000).toFixed(2),
          paidAmount: Math.random() > 0.3 ? (Math.random() * 50000 + 10000).toFixed(2) : 0,
          status: ['Paid', 'Pending', 'Overdue', 'Cancelled'][Math.floor(Math.random() * 4)],
          paymentType: ['Application Fee', 'Assessment Fee', 'Annual Fee', 'Certificate Fee'][Math.floor(Math.random() * 4)],
          gstAmount: ((Math.random() * 50000 + 10000) * 0.18).toFixed(2),
          totalAmount: ((Math.random() * 50000 + 10000) * 1.18).toFixed(2),
          contactPerson: `Contact Person ${i}`,
          email: `contact${i}@lab.com`,
          phone: `9876543${String(i).padStart(3, '0')}`,
          address: `Address ${i}, Lab Street, City`,
          remarks: Math.random() > 0.5 ? 'Regular payment' : '',
        });
      }

      setInvoices(mockInvoices);
      setTotalRecords(mockInvoices.length);
    } catch (error) {
      showSnackbar('Error loading invoices', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    // Apply filters and reload data
    loadInvoices();
    showSnackbar('Filters applied successfully', 'success');
  };

  const handleReset = () => {
    setFilters({
      invoiceNo: '',
      labName: '',
      status: '',
      dateFrom: null,
      dateTo: null,
    });
    loadInvoices();
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setDialogOpen(true);
  };

  const handleDownloadInvoice = (invoice) => {
    showSnackbar(`Downloading invoice ${invoice.invoiceNo}`, 'info');
  };

  const handlePrintInvoice = (invoice) => {
    showSnackbar(`Printing invoice ${invoice.invoiceNo}`, 'info');
  };

  const handleEmailInvoice = (invoice) => {
    showSnackbar(`Sending invoice ${invoice.invoiceNo} to ${invoice.email}`, 'info');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (status) => {
    const colorMap = {
      Paid: 'success',
      Pending: 'warning',
      Overdue: 'error',
      Cancelled: 'default',
    };
    return (
      <Chip
        label={status}
        color={colorMap[status] || 'default'}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getCurrentPageData = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return invoices.slice(startIndex, endIndex);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2 }}>
        {/* Filter Section */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Invoice Management Filters"
            action={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => showSnackbar('Generate new invoice functionality will be implemented', 'info')}
              >
                Generate Invoice
              </Button>
            }
          />
          <CardContent>
            <Grid container spacing={2} alignItems="end">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Invoice No"
                  value={filters.invoiceNo}
                  onChange={(e) => handleFilterChange('invoiceNo', e.target.value)}
                  placeholder="Enter Invoice No"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Laboratory Name"
                  value={filters.labName}
                  onChange={(e) => handleFilterChange('labName', e.target.value)}
                  placeholder="Enter Lab Name"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Overdue">Overdue</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Date From"
                  value={filters.dateFrom}
                  onChange={(newValue) => handleFilterChange('dateFrom', newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Date To"
                  value={filters.dateTo}
                  onChange={(newValue) => handleFilterChange('dateTo', newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    size="small"
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <SearchIcon />
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    size="small"
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <RefreshIcon />
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Invoice Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {invoices.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {invoices.filter(inv => inv.status === 'Paid').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Paid Invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {invoices.filter(inv => inv.status === 'Pending').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" color="error.main">
                  {invoices.filter(inv => inv.status === 'Overdue').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overdue Invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Invoices Table */}
        <Card>
          <CardContent>
            {invoices.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No invoices found. Try adjusting your filters.
              </Alert>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Actions</TableCell>
                        <TableCell>Invoice No</TableCell>
                        <TableCell>Laboratory</TableCell>
                        <TableCell>Lab ID</TableCell>
                        <TableCell>Invoice Date</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Payment Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>GST</TableCell>
                        <TableCell>Total Amount</TableCell>
                        <TableCell>Paid Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Contact</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getCurrentPageData().map((invoice) => (
                        <TableRow key={invoice.id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="View Invoice">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewInvoice(invoice)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadInvoice(invoice)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Print">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePrintInvoice(invoice)}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Email">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEmailInvoice(invoice)}
                                >
                                  <EmailIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell>{invoice.invoiceNo}</TableCell>
                          <TableCell>{invoice.labName}</TableCell>
                          <TableCell>{invoice.labId}</TableCell>
                          <TableCell>{invoice.invoiceDate}</TableCell>
                          <TableCell>{invoice.dueDate}</TableCell>
                          <TableCell>{invoice.paymentType}</TableCell>
                          <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                          <TableCell>{formatCurrency(invoice.gstAmount)}</TableCell>
                          <TableCell>{formatCurrency(invoice.totalAmount)}</TableCell>
                          <TableCell>{formatCurrency(invoice.paidAmount)}</TableCell>
                          <TableCell>{getStatusChip(invoice.status)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {invoice.contactPerson}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {invoice.email}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Invoice Detail Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Invoice Details - {selectedInvoice?.invoiceNo}
          </DialogTitle>
          <DialogContent dividers>
            {selectedInvoice && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Laboratory Information
                  </Typography>
                  <Typography variant="body1">{selectedInvoice.labName}</Typography>
                  <Typography variant="body2">{selectedInvoice.labId}</Typography>
                  <Typography variant="body2">{selectedInvoice.address}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contact Information
                  </Typography>
                  <Typography variant="body2">{selectedInvoice.contactPerson}</Typography>
                  <Typography variant="body2">{selectedInvoice.email}</Typography>
                  <Typography variant="body2">{selectedInvoice.phone}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Invoice Information
                  </Typography>
                  <Typography variant="body2">Invoice Date: {selectedInvoice.invoiceDate}</Typography>
                  <Typography variant="body2">Due Date: {selectedInvoice.dueDate}</Typography>
                  <Typography variant="body2">Payment Type: {selectedInvoice.paymentType}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Amount Details
                  </Typography>
                  <Typography variant="body2">Base Amount: {formatCurrency(selectedInvoice.amount)}</Typography>
                  <Typography variant="body2">GST Amount: {formatCurrency(selectedInvoice.gstAmount)}</Typography>
                  <Typography variant="h6" color="primary">
                    Total Amount: {formatCurrency(selectedInvoice.totalAmount)}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => handleDownloadInvoice(selectedInvoice)}
            >
              Download
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default InvoiceManagement;